import React, { useEffect, useRef } from 'react';
import s from './styles/StudentDrawer.module.scss';

export const StudentDrawer = ({ student, onClose }) => {
  const drawerRef = useRef(null);

  useEffect(() => {
    if (drawerRef.current) {
      drawerRef.current.focus();
    }
  }, []);

  if (!student) return null;

  return (
    <>
      <div className={s.backdrop} onClick={onClose} aria-hidden="true" />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-drawer-title"
        tabIndex={-1}
        ref={drawerRef}
        className={s.drawer}
      >
        <div className={s.header}>
          <div className={s.headerMain}>
            <button
              className={s.backBtn}
              onClick={onClose}
              aria-label="Close drawer"
              autoFocus
            >
              <span className={`material-icons ${s.icon}`}>arrow_back</span>
            </button>

            <img
              alt={student.name}
              className={s.avatar}
              src={student.avatar ?? '/default-avatar.png'}
            />

            <div className={s.titleBlock}>
              <h1
                id="student-drawer-title"
                className={s.title}
                title={`${student.name}'s Dashboard`}
              >
                {student.name}'s Dashboard
              </h1>

              <div className={s.badges}>
                <span className={s.badge}>Grade {student.grade}</span>
                <span className={s.statusBadge}>
                  <span className={s.statusDot} />
                  On Track
                </span>
              </div>
            </div>
          </div>

          <div className={s.headerActions}>
            <a
              href="#session-notes-section"
              className={s.linkButton}
            >
              <span className={`material-icons ${s.iconSmall}`}>add</span>
              Add Notes
            </a>
            <button className={s.primaryButton}>
              <span className={`material-icons ${s.iconSmall}`}>
                chat_bubble
              </span>
              Message
            </button>
          </div>
        </div>

        <div className={s.content}>
          <section className={s.topSection}>
            <div className={s.topCardsColumn}>
              <div className={s.topCard}>
                <span
                  className={`material-icons ${s.topCardIcon}`}
                  style={{ color: '#c4b5fd' }}
                >
                  work_outline
                </span>
                <p className={s.topCardLabel}>Top Career</p>
                <h3 className={s.topCardValue}>Comp. Sci</h3>
              </div>

              <div className={s.topCard}>
                <span
                  className={`material-icons ${s.topCardIcon}`}
                  style={{ color: '#4ade80' }}
                >
                  public
                </span>
                <p className={s.topCardLabel}>Top Country</p>
                <h3 className={s.topCardValue}>Canada</h3>
              </div>

              <div className={s.topCard}>
                <span
                  className={`material-icons ${s.topCardIcon}`}
                  style={{ color: '#facc15' }}
                >
                  school
                </span>
                <p className={s.topCardLabel}>Top Course</p>
                <h3 className={s.topCardValue}>B.Sc. Hon.</h3>
              </div>
            </div>

            <div className={s.readinessCard}>
              <div className={s.readinessCircle}>
                <svg className={s.readinessSvg} viewBox="0 0 100 100">
                  <circle
                    className={s.readinessTrack}
                    cx="50"
                    cy="50"
                    fill="transparent"
                    r="45"
                    stroke="currentColor"
                    strokeDasharray="282.7"
                    strokeDashoffset="0"
                    strokeWidth="8"
                  />
                  <circle
                    className={`gauge-arc-animated`}
                    cx="50"
                    cy="50"
                    fill="transparent"
                    r="45"
                    stroke="currentColor"
                    strokeDasharray="282.7"
                    strokeDashoffset={`calc(282.7 - (282.7 * ${student.readinessPercent}) / 100)`}
                    strokeLinecap="round"
                    strokeWidth="8"
                    style={{
                      transform: 'rotate(-90deg)',
                      transformOrigin: '50% 50%',
                    }}
                  />
                </svg>
                <div className={s.readinessCenter}>
                  <span className={s.readinessValue}>
                    {student.readinessPercent}%
                  </span>
                  <span className={s.readinessLabel}>Readiness</span>
                </div>
              </div>
            </div>
          </section>

          <section className={s.performanceGrid}>
            <div className={s.performanceCard}>
              <div className={s.performanceGroup}>
                <h4 className={s.performanceLabel}>IELTS Performance</h4>
                <div className={s.progressTrack}>
                  <div
                    className={s.progressFillGreen}
                    style={{ width: `${student.ieltsBand * 10}%` }}
                  />
                </div>
                <p className={s.performanceHint}>
                  Overall Band: {student.ieltsBand} (Target:{' '}
                  {student.ieltsTarget})
                </p>
              </div>

              <div className={s.performanceGroup}>
                <h4 className={s.performanceLabel}>SOP Progress</h4>
                <div className={s.progressTrack}>
                  <div
                    className={s.progressFillPurple}
                    style={{ width: `${student.sopProgress}%` }}
                  />
                </div>
                <p className={s.performanceHint}>{student.sopStatus}</p>
              </div>
            </div>

            <div className={s.summaryCard}>
              <h4 className={s.summaryTitle}>AI Summary</h4>
              <p className={s.summaryText}>{student.aiSummary}</p>
            </div>
          </section>

          <section className={s.journeyWrapper}>
            <h2 className={s.sectionTitle}>Journey Explorer</h2>

            <div className={s.journeyLayout}>
              <div className={s.journeyList}>
                {student.journeyItems.map((item, idx) => {
                  const itemClass = [
                    s.journeyItem,
                    item.active ? s.journeyItemActive : s.journeyItemInactive,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  const fillClass = [
                    s.journeyProgressFill,
                    item.active
                      ? s.journeyProgressFillActive
                      : s.journeyProgressFillPending,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <button key={idx} className={itemClass}>
                      <h4 className={s.journeyTitle}>{item.title}</h4>
                      <p className={s.journeySubtitle}>{item.subtitle}</p>
                      <div className={s.journeyProgressTrack}>
                        <div
                          className={fillClass}
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className={s.journeyDetail}>
                <div className={s.journeySteps}>
                  <div className={s.journeyStep}>
                    <div
                      className={`${s.journeyStepCircle} ${s.journeyStepCircleGreen}`}
                    >
                      <span className={`material-icons ${s.iconSmall}`}>
                        done
                      </span>
                    </div>
                    <p className={s.journeyStepLabel}>Career</p>
                  </div>

                  <div
                    className={`${s.journeyStepLine} ${s.journeyStepLineGreen}`}
                  />

                  <div className={s.journeyStep}>
                    <div
                      className={`${s.journeyStepCircle} ${s.journeyStepCircleGreen}`}
                    >
                      <span className={`material-icons ${s.iconSmall}`}>
                        done
                      </span>
                    </div>
                    <p className={s.journeyStepLabel}>Country</p>
                  </div>

                  <div
                    className={`${s.journeyStepLine} ${s.journeyStepLineGreen}`}
                  />

                  <div className={s.journeyStep}>
                    <div
                      className={`${s.journeyStepCircle} ${s.journeyStepCirclePurple}`}
                    >
                      3
                    </div>
                    <p
                      className={`${s.journeyStepLabel} ${s.journeyStepLabelPurple}`}
                    >
                      Course
                    </p>
                  </div>

                  <div
                    className={`${s.journeyStepLine} ${s.journeyStepLineGray}`}
                  />

                  <div className={`${s.journeyStep} ${s.journeyStepDimmed}`}>
                    <div
                      className={`${s.journeyStepCircle} ${s.journeyStepCircleGray}`}
                    >
                      4
                    </div>
                    <p className={s.journeyStepLabel}>College</p>
                  </div>

                  <div
                    className={`${s.journeyStepLine} ${s.journeyStepLineGray}`}
                  />

                  <div className={`${s.journeyStep} ${s.journeyStepDimmed}`}>
                    <div
                      className={`${s.journeyStepCircle} ${s.journeyStepCircleGray}`}
                    >
                      5
                    </div>
                    <p className={s.journeyStepLabel}>Applications</p>
                  </div>
                </div>

                <div>
                  <h4 className={s.decisionsTitle}>Decisions</h4>
                  <div className={s.decisionsList}>
                    <p>
                      <span className={s.decisionsItemLabel}>Career:</span>
                      {student.decisions.career}
                    </p>
                    <p>
                      <span className={s.decisionsItemLabel}>Country:</span>
                      {student.decisions.country}
                    </p>
                    <p>
                      <span className={s.decisionsItemLabel}>Course:</span>
                      {student.decisions.course}
                    </p>
                    <p>
                      <span className={s.decisionsItemLabel}>College:</span>
                      {student.decisions.college}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="session-notes-section" className={s.notesSection}>
            <h2 className={s.sectionTitle}>Session Notes</h2>

            <div className={s.notesCard}>
              <div className={s.notesInputRow}>
                <span
                  className={`material-icons ${s.notesInputIcon}`}
                >
                  edit_note
                </span>
                <input
                  type="text"
                  className={s.notesInput}
                  placeholder="Add a new session note..."
                />
                <button className={s.notesAddBtn}>Add</button>
              </div>

              <div className={s.notesList}>
                <div className={s.noteItem}>
                  <div className={s.noteHeader}>
                    <p className={s.noteTitle}>
                      Session with Ahmed - March 15, 2024
                    </p>
                    <span className={s.noteMeta}>by You</span>
                  </div>
                  <p className={s.noteText}>
                    Discussed U of T vs. Waterloo. Ahmed is leaning towards
                    Waterloo due to their co-op program. We reviewed his SOP for
                    Waterloo and made minor adjustments. He will send the final
                    draft by tomorrow EOD.
                  </p>
                </div>

                <div className={s.noteItem}>
                  <div className={s.noteHeader}>
                    <p className={s.noteTitle}>
                      Quick Check-in - March 12, 2024
                    </p>
                    <span className={s.noteMeta}>by You</span>
                  </div>
                  <p className={s.noteText}>
                    Followed up on the IELTS score submission. All confirmed.
                  </p>
                </div>
              </div>

              <button className={s.notesMoreBtn}>View all notes</button>
            </div>
          </section>

          <div className={s.addSessionWrapper}>
            <button className={s.addSessionBtn}>
              <span className={`material-icons ${s.icon}`}>
                add_circle_outline
              </span>
              <span>Add Session</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
